import { WorkflowEventTypes } from '@amzn/awscat-pattern-library-service-model';
import { createWorkflowEventWithStatePolling } from '../../../api/CreateWorkflowEventWithStatePolling';
import ReadonlyContentData from './ReadonlyContentData';

/**
 * DRAFT-SPECIFIC readonly view of content.
 *
 * Extends ReadonlyContentData (which renders the full readonly page with tabs:
 * Latest/History/UpdateRequests, track-changes diff, bar-raiser diff, polling, etc.)
 *
 * This subclass only overrides TWO things:
 *
 * 1. showDiff = true
 *    Enables the track-changes polling system in ReadonlyContentData.componentDidMount().
 *    When a reviewer is viewing content (IN_REVIEW state), the parent class polls the
 *    backend to detect if the author is currently editing or has made changes since the
 *    reviewer started viewing. If changes are detected, it shows a "View Changes" bar
 *    with a diff view. This is only relevant for draft workflows (not published content).
 *
 * 2. sendWorkflowLockEvent()
 *    Called when the user clicks "Edit" on the readonly view (via ReadonlyContentData.enableEditing).
 *    Sends a LOCK event to the DRAFT workflow to acquire the edit lock.
 *    The parent class's default implementation is a no-op.
 *    Other subclasses (e.g. for published content) would send different event types.
 *
 * Inheritance chain:
 *   BaseContentData (shared utilities: clone, history, navigation, tab handling)
 *     └── ReadonlyContentData (full readonly page: tabs, diff, polling, inline comments)
 *           └── DraftReadonlyContentData (THIS: enables diff + sends LOCK on draft workflow)
 */
class DraftReadonlyContentData extends ReadonlyContentData {
  // Enable the track-changes diff system (polling + "View Changes" / "Refresh Page" bar).
  // Without this, ReadonlyContentData.componentDidMount() skips pollWorkflow().
  protected showDiff = true;

  // Acquire the edit lock on the DRAFT workflow when user clicks "Edit".
  // Uses createWorkflowEventWithStatePolling which sends the event and then polls
  // until the workflow state reflects the lock acquisition (eventual consistency).
  // @ts-expect-error TS2416 - strict migration
  protected sendWorkflowLockEvent = async () => {
    const { workflow } = this.props;
    // @ts-expect-error TS2339 - strict migration
    const { contentId, draftWorkflowId } = workflow;
    await createWorkflowEventWithStatePolling(
      contentId,
      draftWorkflowId,
      WorkflowEventTypes.LOCK,
      '',
    );
  };
}

export default DraftReadonlyContentData;
